import type { CSSProperties } from "react";
import { toCssVars, type Branding } from "@gatiq/shared";
import { Button } from "@/ui";

/**
 * A miniature login card rendered with the *pending* (unsaved) token values,
 * scoped to this wrapper only via inline CSS custom properties — it must
 * never repaint the rest of the page while the org admin is still editing.
 *
 * Every class below (`bg-brand`, `text-ink`, `font-title`, `rounded-md`…)
 * resolves through the same `--gq-*` custom properties the whole app reads,
 * so setting them on this wrapper re-themes only what's inside it.
 */
export function BrandPreview({ branding }: { branding: Branding }) {
  const scoped = toCssVars(branding) as CSSProperties;

  return (
    /* No border on this canvas: it already sits inside the preview Card, and
       a second rounded outline around a card that itself contains a card read
       as three nested boxes. The `bg-surface` fill is the point — it shows the
       branding's own page colour behind the mock. */
    <div className="rounded-lg bg-surface p-4" style={scoped}>
      <div className="mx-auto flex w-full max-w-[260px] flex-col xl:max-w-[300px] items-center gap-3 rounded-md border border-rule bg-raised px-5 py-5 text-center shadow-card">
        {branding.logo ? (
          <img alt="" className="h-9 max-w-[160px] object-contain" src={branding.logo} />
        ) : (
          <p className="font-title text-base font-semibold text-ink">{branding.appName || "GatiQ Rewards"}</p>
        )}

        <div>
          <p className="font-title text-sm font-semibold text-ink">Welcome back</p>
          <p className="text-xs text-muted">Sign in to continue</p>
        </div>

        <div className="flex w-full flex-col gap-1.5 text-left">
          <div className="h-9 w-full rounded-md border border-rule bg-surface px-3 text-xs leading-9 text-muted">
            Email
          </div>
          <div className="h-9 w-full rounded-md border border-rule bg-surface px-3 text-xs leading-9 text-muted">
            Password
          </div>
        </div>

        <Button block className="pointer-events-none" size="sm" tabIndex={-1} type="button">
          Sign in
        </Button>

        {branding.supportPhone ? (
          <p className="text-xs text-brand-ink">Need help? {branding.supportPhone}</p>
        ) : null}

        <p className="text-xs text-muted">Powered by {branding.appName || "GatiQ Rewards"}</p>
      </div>
    </div>
  );
}
